import React, { useState, useRef, useEffect } from "react";
import {
  TouchableOpacity,
  Text,
  StyleSheet,
  View,
  Animated,
  Pressable,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useAuth } from "../../contexts/AuthContext";
import { useNavigation } from "@react-navigation/native";

const AuthButton: React.FC = () => {
  const { isAuthenticated, currentUser, logout } = useAuth();
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const dropdownOpacity = useRef(new Animated.Value(0)).current;
  const navigation = useNavigation<any>();

  useEffect(() => {
    if (dropdownVisible) {
      Animated.timing(dropdownOpacity, {
        toValue: 1,
        duration: 200,
        useNativeDriver: true,
      }).start();
    } else {
      Animated.timing(dropdownOpacity, {
        toValue: 0,
        duration: 150,
        useNativeDriver: true,
      }).start();
    }
  }, [dropdownVisible]);

  const navigateToLogin = () => {
    navigation.navigate("Login");
  };

  const toggleDropdown = () => {
    setDropdownVisible(!dropdownVisible);
  };

  const closeDropdown = () => {
    setDropdownVisible(false);
  };

  const handleLogout = async () => {
    try {
      await logout();
      closeDropdown();
    } catch (error) {
      console.error("Error logging out:", error);
    }
  };

  const handleOrganizationDashboard = () => {
    // Open organization dashboard in new tab
    if (typeof window !== 'undefined') {
      window.open('/organization/dashboard', '_blank');
    }
    closeDropdown();
  };

  // Helper to get username safely
  const getUsername = () => {
    if (!currentUser) return "User";

    // Handle Parse User object
    if (currentUser.attributes) {
      return currentUser.attributes.username || "User";
    }

    // Regular object
    return currentUser.username || "User";
  };

  const username = getUsername();
  const initial = username.charAt(0).toUpperCase();

  // Check if user is organization type
  const isOrganization = currentUser && 
    (currentUser.get ? currentUser.get('userType') === 'organization' : 
     currentUser.userType === 'organization');

  return (
    <>
      {isAuthenticated ? (
        <View data-oid="idb7l1p">
          <TouchableOpacity
            style={styles.profileButton}
            onPress={toggleDropdown}
            data-oid="ey7-spw"
          >
            <View style={styles.avatarContainer} data-oid="ftrt.42">
              <Text style={styles.avatarText} data-oid="ibsrriw">
                {initial}
              </Text>
            </View>
          </TouchableOpacity>

          {dropdownVisible && (
            <>
              <Pressable
                style={styles.dropdownOverlay}
                onPress={closeDropdown}
                data-oid="ie2xdbd"
              />

              <Animated.View
                style={[styles.dropdownMenu, { opacity: dropdownOpacity }]}
                data-oid="li5b98o"
              >
                <Text style={styles.dropdownUsername} data-oid="ex4x26q">
                  {username}
                </Text>
                
                {isOrganization && (
                  <TouchableOpacity
                    style={styles.dropdownItem}
                    onPress={handleOrganizationDashboard}
                    data-oid="org-dashboard"
                  >
                    <Ionicons
                      name="business-outline"
                      size={18}
                      color="#2563EB"
                      data-oid="org-icon"
                    />

                    <Text style={styles.organizationText} data-oid="org-text">
                      Manage Organization
                    </Text>
                  </TouchableOpacity>
                )}
                
                <TouchableOpacity
                  style={styles.dropdownItem}
                  onPress={handleLogout}
                  data-oid="6werf6x"
                >
                  <Ionicons
                    name="log-out-outline"
                    size={18}
                    color="#EF4444"
                    data-oid=".fpyld7"
                  />

                  <Text style={styles.logoutText} data-oid="obdry:y">
                    Log Out
                  </Text>
                </TouchableOpacity>
              </Animated.View>
            </>
          )}
        </View>
      ) : (
        <TouchableOpacity
          style={styles.signInButton}
          onPress={navigateToLogin}
          data-oid="z077gz."
        >
          <Text style={styles.signInText} data-oid="v3klth7">
            Sign In
          </Text>
          <Ionicons
            name="log-in-outline"
            size={16}
            color="#FFFFFF"
            style={styles.signInIcon}
            data-oid="1vzbrvd"
          />
        </TouchableOpacity>
      )}
    </>
  );
};

const styles = StyleSheet.create({
  signInButton: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#272727",
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 8,
    marginLeft: 8,
  },
  signInText: {
    color: "#FFFFFF",
    fontSize: 14,
    fontWeight: "600",
    fontFamily: "Inter-SemiBold",
  },
  signInIcon: {
    marginLeft: 4,
  },
  profileButton: {
    padding: 2,
    zIndex: 10,
  },
  avatarContainer: {
    width: 38,
    height: 38,
    borderRadius: 19,
    backgroundColor: "#272727",
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 2,
    borderColor: "#F5F5F5",
  },
  avatarText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "bold",
    fontFamily: "Inter-Bold",
  },
  dropdownOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 9,
  },
  dropdownMenu: {
    position: "absolute",
    top: 45,
    left: -100,
    backgroundColor: "#FFFFFF",
    minWidth: 150,
    paddingVertical: 8,
    borderRadius: 8,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 5,
    zIndex: 11,
  },
  dropdownUsername: {
    fontSize: 14,
    color: "#272727",
    fontWeight: "600",
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: "#F5F5F5",
    fontFamily: "Inter-SemiBold",
  },
  dropdownItem: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 16,
    paddingVertical: 10,
  },
  organizationText: {
    color: "#2563EB",
    fontSize: 14,
    fontWeight: "500",
    marginLeft: 8,
    fontFamily: "Inter-Medium",
  },
  logoutText: {
    color: "#EF4444",
    fontSize: 14,
    fontWeight: "500",
    marginLeft: 8,
    fontFamily: "Inter-Medium",
  },
});

export default AuthButton;
